import { Resource } from '@ngxp/rest';
import { RegisterName } from '@schir-int-client/register-shared';
import { KontaktResource } from '@schir-int-client/adressverwaltung-shared';
import { VerfahrenSearchKey } from './verfahren.messages';
import { ListResource, PagedListResource } from '@schir-int-client/ngrx-helpers';
import { VerfahrenLinkRel, ZaehlblattEntryListLinkRel } from './verfahren.linkrel';

export enum VerfahrenStatus {
	VORLAEUFIG = 'VORLAEUFIG',
	EINGETRAGEN = 'EINGETRAGEN',
	GELOESCHT = 'GELOESCHT',
	GESCHLOSSEN = 'GESCHLOSSEN',
	ABGELEHNT = 'ABGELEHNT',
}

export interface VerfahrenSearchQuery {
	searchString: string;
	searchIn: VerfahrenSearchKey[];
	filterByStatus: VerfahrenStatus[];
	limit?: number;
}

export interface NewVerfahren {
	register: RegisterName;
	blattNummer: number;
}

export interface VerfahrenStammdaten {
	aktenzeichen: string;
	schiffName: string;
	unsignedSchiffName: string;
	imoNummer: string;
	unsignedImoNummer: string;
	eigentuemerNamen: string;
	unsignedEigentuemerNamen: string;
	unterscheidungssignal: string;
	unsignedUnterscheidungssignal: string;
	historischeSchiffNamen: string;
	standortAkte: string;
	arKennzeichen: string;
	eintragungszeichen: string;
	unsignedEintragungszeichen: string;
}

export interface Verfahren extends NewVerfahren {
	aktenzeichen: string;
	amtsgerichtNummer: number;
	status: VerfahrenStatus;
	aufschrift: string;
	notiz: string;
	verfahrenKontakte: KontaktResource[];
	stammdaten: VerfahrenStammdaten;
	schnellzugriff?: { verfahrenId: number; key: string; value: string }[];
	properties?: VerfahrenProperty[];
	zuordnungen?: {
		hauptverfahren: VerfahrenReferenz;
		untergeordneteVerfahren: VerfahrenReferenz[];
	};
}

export interface VerfahrenReferenz {
	aktenzeichen: string;
	verfahrenId: number;
}

export interface VerfahrenReferenzListResource {
	_embedded: { aktenzeichenAndIdList: VerfahrenReferenz[] };
	page: { totalElements: number };
}

export interface VerfahrenProperty {
	key: string;
	value: string;
	updatedAt: string;
}

export interface VerfahrenResource extends Verfahren, Resource {}

export interface VerfahrenListResource extends PagedListResource<VerfahrenResource, VerfahrenLinkRel.VERFAHREN_LIST> {}

export enum ZaehlblattAktionTyp {
	EIGENTUEMER = 'EIGENTUEMER',
	LFZ_NEU = 'LFZ_NEU',
	LOESCHUNG_PFANDRECHT = 'LOESCHUNG_PFANDRECHT',
	PFANDRECHT_NEU = 'PFANDRECHT_NEU',
	PFANDRECHTE_AN_BESTEHENDEN_LFZ = 'PFANDRECHTE_AN_BESTEHENDEN_LFZ',
	SCHLIESSUNG_DES_BLATTES = 'SCHLIESSUNG_DES_BLATTES',
	EINGEREICHTE_URKUNDE_ZUM_REGISTER = 'EINGEREICHTE_URKUNDE_ZUM_REGISTER',
	SONSTIGE = 'SONSTIGE',
	EINGEREICHTE_URKUNDEN_SSR = 'EINGEREICHTE_URKUNDEN_SSR',
	EINGEREICHTE_URKUNDEN_BSR = 'EINGEREICHTE_URKUNDEN_BSR',
	EINGEREICHTE_URKUNDEN_SBR = 'EINGEREICHTE_URKUNDEN_SBR',
	EINGEREICHTE_URKUNDEN_AR = 'EINGEREICHTE_URKUNDEN_AR',
	ANLEGUNG_ETL_UND_PFANDRECHTSVOLLSTRECKUNG = 'ANLEGUNG_ETL_UND_PFANDRECHTSVOLLSTRECKUNG',
	VERAENDERUNGEN_AM_ETL = 'VERAENDERUNGEN_AM_ETL',
	VERAENDERUNGEN_AM_PFANDRECHT_ETL = 'VERAENDERUNGEN_AM_PFANDRECHT_ETL',
	SCHLIESSUNG_ETL = 'SCHLIESSUNG_ETL',
	EINGEREICHTE_URKUNDEN_ZUM_REGISTER_ETL = 'EINGEREICHTE_URKUNDEN_ZUM_REGISTER_ETL',
}

// iterating Object.values(ZaehlblattAktionTyp) would not guarantee the order
export const orderedZaehlblattAktionTypes = [
	ZaehlblattAktionTyp.EIGENTUEMER,
	ZaehlblattAktionTyp.LFZ_NEU,
	ZaehlblattAktionTyp.LOESCHUNG_PFANDRECHT,
	ZaehlblattAktionTyp.PFANDRECHT_NEU,
	ZaehlblattAktionTyp.PFANDRECHTE_AN_BESTEHENDEN_LFZ,
	ZaehlblattAktionTyp.SCHLIESSUNG_DES_BLATTES,
	ZaehlblattAktionTyp.EINGEREICHTE_URKUNDE_ZUM_REGISTER,
	ZaehlblattAktionTyp.SONSTIGE,
	ZaehlblattAktionTyp.EINGEREICHTE_URKUNDEN_SSR,
	ZaehlblattAktionTyp.EINGEREICHTE_URKUNDEN_BSR,
	ZaehlblattAktionTyp.EINGEREICHTE_URKUNDEN_SBR,
	ZaehlblattAktionTyp.EINGEREICHTE_URKUNDEN_AR,
	ZaehlblattAktionTyp.ANLEGUNG_ETL_UND_PFANDRECHTSVOLLSTRECKUNG,
	ZaehlblattAktionTyp.VERAENDERUNGEN_AM_ETL,
	ZaehlblattAktionTyp.VERAENDERUNGEN_AM_PFANDRECHT_ETL,
	ZaehlblattAktionTyp.SCHLIESSUNG_ETL,
	ZaehlblattAktionTyp.EINGEREICHTE_URKUNDEN_ZUM_REGISTER_ETL,
];

export type ZaehlblattAktionen = Record<ZaehlblattAktionTyp, number>;

export type RegisterAusdruckType = 'einfach' | 'beglaubigt' | 'entwurf';

export interface ZaehlblattEntryResource extends Resource {
	type: string;
	createdAt: string;
}

export interface ZaehlblattEntryListResource extends ListResource<
	ZaehlblattEntryResource,
	ZaehlblattEntryListLinkRel.ZAEHLBLATT_ENTRY_LIST
> {}
